import {
  ChoiceItem,
  DisplayLogicExpression,
  QualtricsSurvey,
  QualtricsSurveyDefinition,
  QuestionLanguage,
  SurveyQuestion,
} from '../../models/qualtrics-feedback-form.model';

type RawChoice = { display?: string; Display?: string };
type RawLanguageEntry = {
  QuestionText?: string;
  Choices?: Record<string, RawChoice>;
  questionText?: string;
};

export class QualtricsSurveyMapperService {
  static mapToSurveyDefinition(
    survey: QualtricsSurvey | null,
  ): QualtricsSurveyDefinition | null {
    const questions = survey?.surveyDetails?.Questions;

    if (!questions || typeof questions !== 'object') {
      return null;
    }

    const mappedElements = Object.values(questions)
      .map((element) => this.mapQuestion(element))
      .filter((question): question is SurveyQuestion => question !== undefined);

    if (mappedElements.length === 0) {
      return null;
    }

    return { elements: mappedElements };
  }

  static normalizeSurveyQuestions(survey: any): Record<string, unknown> {
    const surveyDetailsEntry = Array.isArray(survey?.surveyDetails)
      ? survey.surveyDetails[0]
      : undefined;
    const questions = surveyDetailsEntry?.Questions;

    if (
      questions &&
      typeof questions === 'object' &&
      !Array.isArray(questions)
    ) {
      return questions as Record<string, unknown>;
    }

    return {};
  }

  private static mapQuestion(
    element: SurveyQuestion,
  ): SurveyQuestion | undefined {
    const questionId = element.QuestionID;
    const questionType = element.QuestionType;
    const selector = element.Selector;

    if (!questionId || !questionType || !selector) {
      return undefined;
    }

    const questionText = element.QuestionText ?? '';

    const mappedQuestion: SurveyQuestion = {
      QuestionID: questionId,
      QuestionText: questionText,
      QuestionType: questionType,
      Selector: selector,
      ForceResponse: element.Validation?.settings?.forceResponse,
      Choices: this.mapChoices(element) ?? [],
      ChoiceOrder: element.ChoiceOrder ?? [],
      Language: this.mapLanguage(element) ?? {},
      DisplayLogic: this.mapDisplayLogic(element.DisplayLogic),
    };

    return mappedQuestion;
  }

  private static mapChoices(element: SurveyQuestion): ChoiceItem[] | undefined {
    const rawElement = element as SurveyQuestion & {
      Choices?: Record<string, RawChoice>;
      ChoiceOrder?: number[];
    };
    const choicesInput = (element.Choices ?? rawElement.Choices) as
      | Array<RawChoice>
      | Record<string, RawChoice>
      | undefined;

    if (!choicesInput) {
      return undefined;
    }

    if (Array.isArray(choicesInput)) {
      const normalizedChoices = choicesInput.map((choice, index) => ({
        display: choice?.display ?? choice?.Display ?? String(index + 1),
      }));

      return normalizedChoices.length ? normalizedChoices : undefined;
    }

    const orderedChoiceIds =
      (element.ChoiceOrder ?? rawElement.ChoiceOrder)?.map((choiceId: number) =>
        String(choiceId),
      ) ?? Object.keys(choicesInput);

    const normalizedChoices = orderedChoiceIds
      .map((choiceId: string) => choicesInput[choiceId])
      .filter((choice): choice is RawChoice => Boolean(choice))
      .map((choice, index) => ({
        display: choice.display ?? choice.Display ?? String(index + 1),
      }));

    return normalizedChoices.length ? normalizedChoices : undefined;
  }

  private static mapLanguage(
    element: SurveyQuestion,
  ): QuestionLanguage | undefined {
    const language = element.Language;

    if (!language) {
      return undefined;
    }

    const mappedLanguage: QuestionLanguage = {};

    Object.keys(language).forEach((locale) => {
      const source = language[locale] as RawLanguageEntry;

      if (!source) {
        return;
      }

      const mappedChoices: Record<string, ChoiceItem> = {};
      const sourceChoices = source.Choices;

      if (sourceChoices) {
        Object.keys(sourceChoices).forEach((choiceId) => {
          const choice = sourceChoices[choiceId] as {
            display?: string;
            Display?: string;
          };
          mappedChoices[choiceId] = {
            display: choice?.display ?? choice?.Display ?? choiceId,
          };
        });
      }

      mappedLanguage[locale] = {
        QuestionText:
          source.QuestionText ??
          (source as { questionText?: string }).questionText ??
          '',
        Choices:
          Object.keys(mappedChoices).length > 0 ? mappedChoices : undefined,
      };
    });

    return Object.keys(mappedLanguage).length > 0 ? mappedLanguage : undefined;
  }

  private static mapDisplayLogic(
    displayLogic: SurveyQuestion['DisplayLogic'],
  ): DisplayLogicExpression | undefined {
    if (!displayLogic) {
      return undefined;
    }

    return displayLogic as DisplayLogicExpression;
  }
}
